import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode
} from "react";

export type VariantChoice = {
  id: string;
  variantId: string;
  quantity: number;
};

export type PreOrderLine = {
  id: string;
  moduleId: string;
  /** Product and picked extras only. A deposit is never part of a price. */
  unitPriceInclVat: number;
  /** The product's deposit plus each picked choice's, per unit, in minor units. */
  depositInclVat: number;
  choices?: VariantChoice[];
};

type OrdersCreateState = {
  preOrderLines: PreOrderLine[];
};

type OrdersCreateAction =
  | { type: "TOGGLE_PREORDER_LINE"; payload: PreOrderLine }
  | { type: "RESET" };

const OrdersCreateContext = createContext<{
  state: OrdersCreateState;
  togglePreOrderLine: (line: PreOrderLine) => void;
  reset: () => void;
} | null>(null);

function reducer(
  state: OrdersCreateState,
  action: OrdersCreateAction
): OrdersCreateState {
  switch (action.type) {
    case "TOGGLE_PREORDER_LINE": {
      const existingLine = state.preOrderLines.find(
        (line) =>
          line.moduleId === action.payload.moduleId &&
          line.id === action.payload.id
      );

      // Case 1: same line => remove it (toggle off)
      if (existingLine) {
        return {
          ...state,
          preOrderLines: state.preOrderLines.filter(
            (line) =>
              !(
                line.moduleId === action.payload.moduleId &&
                line.id === action.payload.id
              )
          )
        };
      }

      // Case 2 & 3:
      // - If another line with the same moduleId exists, we replace it.
      // - If none exists, we just add this one.
      // We ensure uniqueness by dropping any line with the same moduleId first.
      const withoutSameModule = state.preOrderLines.filter(
        (line) => line.moduleId !== action.payload.moduleId
      );

      return {
        ...state,
        preOrderLines: [...withoutSameModule, action.payload]
      };
    }
    case "RESET":
      return { preOrderLines: [] };
    default:
      return state;
  }
}

export const OrdersCreateProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, { preOrderLines: [] });

  const togglePreOrderLine = useCallback((line: PreOrderLine) => {
    dispatch({ type: "TOGGLE_PREORDER_LINE", payload: line });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const value = useMemo(
    () => ({
      state,
      togglePreOrderLine,
      reset
    }),
    [state, togglePreOrderLine, reset]
  );

  return (
    <OrdersCreateContext.Provider value={value}>
      {children}
    </OrdersCreateContext.Provider>
  );
};

export function useCollectPreOrderLines() {
  const ctx = useContext(OrdersCreateContext);
  if (!ctx)
    throw new Error(
      "useCollectPreOrderLines must be used inside CollectProvider"
    );
  return ctx.state.preOrderLines;
}

/**
 * `togglePreOrderLine` is unguarded: adding a line must go through
 * `takeCandidate` from `use-collect-debt-gate`, which prices it against the
 * wallet first. Removing a line needs no gate.
 */
export function useOrdersCreateActions() {
  const ctx = useContext(OrdersCreateContext);
  if (!ctx)
    throw new Error("useCollectActions must be used inside CollectProvider");
  return {
    togglePreOrderLine: ctx.togglePreOrderLine,
    reset: ctx.reset
  };
}
